import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { groupLifeService, type GroupLifeQuoteDetails } from '@/services';
import { useQuery } from '@tanstack/react-query';
import { useQuotationVersionId } from './use-quotation-version-id.hook';

/**
 * Carrega o snapshot completo da cotação Group Life (inclui statusPortal e
 * calculation). Compartilha queryKey com `useGroupLifeCalculation` e
 * `useHydrateGroupLifeQuote`.
 *
 * Quando a URL traz `quotationVersionId`, busca o snapshot daquela versão
 * (`getQuoteAtVersion`) sob uma queryKey própria — o comportamento sem o param
 * é idêntico ao anterior (versão atual da cotação).
 */
export function useGroupLifeQuoteDetails(enabled = true) {
  const { policyId } = useQuoteParams();
  const versionId = useQuotationVersionId();

  return useQuery({
    queryKey: versionId
      ? ['group-life', 'quote-at-version', policyId, versionId]
      : ['group-life', 'quote-details', policyId],
    queryFn: async (): Promise<GroupLifeQuoteDetails> => {
      if (!policyId) throw new Error('policyId é obrigatório');
      const result = versionId
        ? await groupLifeService.getQuoteAtVersion(policyId, versionId)
        : await groupLifeService.getQuoteDetails(policyId);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Falha ao carregar a cotação');
      }
      return result.data;
    },
    enabled: Boolean(policyId) && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}
